/**
 * Plan picker — drives the product form's variant id + selling_plan
 * from a single flat list of variant×plan combos.
 *
 * sprint-016: also owns the Sunshine add-on. When the add-on is checked the
 * native form submit is intercepted and replaced with ONE JSON /cart/add.js
 * call carrying both lines — Refresh (with selling_plan) and Sunshine (one-time,
 * stamped with the _sunshine_subscription line property the billing engine's
 * ingestion keys on). Unchecked = native flow, untouched.
 */
class PlanPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.formId = this.dataset.formId;
    this.sectionId = this.dataset.section;
    this.sunshineVariantId = this.dataset.sunshineVariantId || '';
    this.addEventListener('change', this.onChange.bind(this));

    // Defer initial selection so <variant-radios> finishes its own
    // connectedCallback first — otherwise our change event fires before
    // the theme's listeners are attached, and the Add to Cart button
    // never gets enabled.
    requestAnimationFrame(() => {
      const first = this.querySelector('input[type="radio"]:not(:disabled)');
      if (first && !this.querySelector('input[type="radio"]:checked')) {
        first.checked = true;
        this.applySelection(first);
      }
      this.updateSunshineCopy();
      this.hookSubmit();
    });
  }

  onChange(event) {
    if (event.target.matches('input[type="radio"]')) {
      this.applySelection(event.target);
      this.updateSunshineCopy();
    }
  }

  applySelection(radio) {
    const variantId = radio.dataset.variantId;
    const variantOption = radio.dataset.variantOption;
    const planId = radio.dataset.planId || '';
    const form = document.getElementById(this.formId);
    if (!form) {
      console.warn('[plan-picker] product form not found:', this.formId);
      return;
    }

    // 1. Set variant id
    const idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = variantId;

    // 2. Set or clear selling_plan
    let planInput = form.querySelector('input[name="selling_plan"]');
    if (!planInput) {
      planInput = document.createElement('input');
      planInput.type = 'hidden';
      planInput.name = 'selling_plan';
      form.appendChild(planInput);
    }
    planInput.value = planId;

    // 3. Sync the (hidden) <variant-radios> so image/URL/price update
    const picker = document.querySelector(
      `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
    );
    if (picker) {
      const matchingRadio = picker.querySelector(`input[type="radio"][value="${CSS.escape(variantOption)}"]`);
      if (matchingRadio && !matchingRadio.checked) {
        matchingRadio.checked = true;
        matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!matchingRadio) {
        const select = picker.querySelector('select');
        if (select) {
          select.value = variantOption;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // 4. Force-enable the submit button (belt + suspenders — the theme
    //    should enable it via the variant-radios sync above, but if
    //    that didn't fire for any reason we want a working cart).
    const submitButtons = form.querySelectorAll('button[type="submit"], [name="add"]');
    submitButtons.forEach((btn) => {
      btn.removeAttribute('disabled');
      btn.classList.remove('disabled');
    });
  }

  /** The selected plan's cadence, expressed for the Sunshine copy. */
  selectedPlan() {
    const checked = this.querySelector('input[type="radio"]:checked');
    if (!checked) return { kind: 'none' };
    if (!checked.dataset.planId) return { kind: 'onetime' };
    const days = parseInt(checked.dataset.intervalDays || '30', 10);
    return { kind: 'sub', days: days >= 90 ? 90 : 30 };
  }

  updateSunshineCopy() {
    const copyEl = this.querySelector('[data-sunshine-copy]');
    if (!copyEl) return;
    const plan = this.selectedPlan();
    if (plan.kind === 'sub' && plan.days === 90) {
      copyEl.textContent = 'A bottle with every delivery';
    } else if (plan.kind === 'sub') {
      copyEl.textContent = 'A bottle every 3 months';
    } else {
      copyEl.textContent = 'One 90-day bottle, ships with this order';
    }
  }

  sunshineChecked() {
    const box = this.querySelector('input[name="sunshine-addon"]');
    return Boolean(box && box.checked && this.sunshineVariantId);
  }

  hookSubmit() {
    const form = document.getElementById(this.formId);
    if (!form || form.dataset.sunshineHooked) return;
    form.dataset.sunshineHooked = 'true';
    // Capture phase so we win over the theme's ProductForm fetch handler.
    form.addEventListener(
      'submit',
      (e) => {
        if (!this.sunshineChecked()) return; // native flow
        e.preventDefault();
        e.stopImmediatePropagation();
        this.addBothToCart(form);
      },
      true
    );
  }

  async addBothToCart(form) {
    const buttons = form.querySelectorAll('button[type="submit"], [name="add"]');
    buttons.forEach((b) => b.setAttribute('disabled', 'disabled'));
    try {
      const variantId = parseInt(form.querySelector('input[name="id"]').value, 10);
      const planInput = form.querySelector('input[name="selling_plan"]');
      const planId = planInput && planInput.value ? parseInt(planInput.value, 10) : null;
      const refreshItem = { id: variantId, quantity: 1 };
      if (planId) refreshItem.selling_plan = planId;
      const items = [
        refreshItem,
        {
          id: parseInt(this.sunshineVariantId, 10),
          quantity: 1,
          // The billing engine enrolls recurring Sunshine ONLY on this property
          // — and only when the Refresh line is a subscription.
          properties: planId ? { _sunshine_subscription: 'yes' } : {},
        },
      ];
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('cart add failed: ' + res.status);
      window.location.href = '/cart';
    } catch (err) {
      console.error('[plan-picker] sunshine add failed, falling back to native submit', err);
      // Fall back to the native single-line flow so the customer is never stuck.
      const box = this.querySelector('input[name="sunshine-addon"]');
      if (box) box.checked = false;
      form.submit();
    } finally {
      buttons.forEach((b) => b.removeAttribute('disabled'));
    }
  }
}

if (!customElements.get('plan-picker')) {
  customElements.define('plan-picker', PlanPicker);
}
